import { TicketContact } from "@jugl-web/rest-api/tickets/models/common-types";
import { useCallback, useMemo, useState } from "react";
import { UpdateTicketContactDialogProps } from "../components/UpdateTicketContactDialog";

interface PendingContactChange {
  currentContact: TicketContact;
  nextContact: TicketContact;
}

interface UseUpdateTicketContactDialogOptions {
  currentContact: TicketContact;
  /** Callback invoked after updating the contact, typically to perform an HTTP mutation. */
  onContactUpdate: (contactId: string) => void;
}

/**
 * Hook to handle the update ticket contact flow which involves the `UpdateTicketContactDialog` component.
 */
export const useUpdateTicketContactDialog = ({
  currentContact,
  onContactUpdate,
}: UseUpdateTicketContactDialogOptions) => {
  const [isUpdateContactDialogOpen, setIsUpdateContactDialogOpen] =
    useState(false);

  const [pendingContactChange, setPendingContactChange] =
    useState<PendingContactChange | null>(null);

  const handleConfirm = useCallback(() => {
    if (!pendingContactChange) {
      return;
    }

    onContactUpdate(pendingContactChange.nextContact.id);
  }, [onContactUpdate, pendingContactChange]);

  const updateTicketContactDialogProps =
    useMemo<UpdateTicketContactDialogProps>(() => {
      return {
        isOpen: isUpdateContactDialogOpen,
        currentContactName: pendingContactChange?.currentContact.name ?? "",
        nextContactName: pendingContactChange?.nextContact.name ?? "",
        onClose: () => setIsUpdateContactDialogOpen(false),
        onConfirm: handleConfirm,
      };
    }, [
      handleConfirm,
      isUpdateContactDialogOpen,
      pendingContactChange?.currentContact.name,
      pendingContactChange?.nextContact.name,
    ]);

  const requestUpdateContact = useCallback(
    (newContact: TicketContact) => {
      setPendingContactChange({
        currentContact,
        nextContact: newContact,
      });

      setIsUpdateContactDialogOpen(true);
    },
    [currentContact]
  );

  return {
    updateTicketContactDialogProps,
    requestUpdateContact,
  };
};
